import { useCallback, useEffect, useRef, useState } from "react";

export interface QueuedOutboundMessage<TAttachment> {
  id: string;
  content: string;
  attachments: TAttachment[];
  queuedAt: number;
}

export interface UseOutboundMessageQueueOptions<TAttachment> {
  /** When true, the agent is still working on a prior user message. */
  isAgentBusy: boolean;
  onSend: (content: string, attachments: TAttachment[]) => Promise<void>;
}

export interface UseOutboundMessageQueueResult<TAttachment> {
  queue: QueuedOutboundMessage<TAttachment>[];
  enqueue: (content: string, attachments: TAttachment[]) => void;
  removeFromQueue: (id: string) => void;
}

/**
 * Holds outbound messages until the agent finishes the current turn, then
 * sends one queued message per busy→idle transition (request-response safe).
 */
export function useOutboundMessageQueue<TAttachment>({
  isAgentBusy,
  onSend,
}: UseOutboundMessageQueueOptions<TAttachment>): UseOutboundMessageQueueResult<TAttachment> {
  const [queue, setQueue] = useState<QueuedOutboundMessage<TAttachment>[]>([]);
  const queueRef = useRef(queue);
  queueRef.current = queue;

  const isAgentBusyRef = useRef(isAgentBusy);
  isAgentBusyRef.current = isAgentBusy;

  const prevAgentBusyRef = useRef(isAgentBusy);

  const onSendRef = useRef(onSend);
  onSendRef.current = onSend;

  const drainingRef = useRef(false);

  const enqueue = useCallback((content: string, attachments: TAttachment[]) => {
    setQueue((prev) => [
      ...prev,
      {
        id: `queued-${Date.now()}-${prev.length}`,
        content,
        attachments,
        queuedAt: Date.now(),
      },
    ]);
  }, []);

  const removeFromQueue = useCallback((id: string) => {
    setQueue((prev) => prev.filter((item) => item.id !== id));
  }, []);

  useEffect(() => {
    const wasBusy = prevAgentBusyRef.current;
    prevAgentBusyRef.current = isAgentBusy;

    if (isAgentBusy || queueRef.current.length === 0 || drainingRef.current) {
      return;
    }

    // Send one queued message each time the agent finishes a turn.
    if (!wasBusy) {
      return;
    }

    const next = queueRef.current[0];
    drainingRef.current = true;
    setQueue((prev) => prev.slice(1));

    void onSendRef
      .current(next.content, next.attachments)
      .catch(() => {
        setQueue((prev) => [next, ...prev]);
      })
      .finally(() => {
        drainingRef.current = false;
      });
  }, [isAgentBusy, queue.length]);

  return { queue, enqueue, removeFromQueue };
}
